import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../common/types';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { useGetReleaseKitQuery } from '../../../../redux/api/fymAPI';
import { RevisionCompare } from '../revisions/RevisionCompare';
import { NewRevisionDialog } from '../revisions/NewRevisionDialog';
import { RevisionEntry } from '../revisions/revisionModel';
import { revisionMessages } from '../revisions/revisionMessages';
import { AssetViewerItem } from './viewerItems';

interface ViewerRevisionLayersProps {
  accountId: number;
  song: CatalogItem;
  /** The item on the stage: the dialog names it, "More options" opens the Studio on it. */
  item: AssetViewerItem;
  label: string;
  entries: RevisionEntry[];
  compareKeys: string[] | null;
  dialogEntry: RevisionEntry | null;
  currentUserId: number | null;
  onCloseCompare: () => void;
  onCloseDialog: () => void;
  onMoreOptions: (item: AssetViewerItem) => void;
  onToast: (message: string) => void;
}

/**
 * The two layers over the viewer (Releases v2): Compare (design d2) and "Make a new revision"
 * (design d3). The theme of a new revision is the theme of the song now (the kit answer), else the
 * theme of the revision it starts from.
 */
const ViewerRevisionLayersComponent: React.FC<ViewerRevisionLayersProps> = (props) => {
  const { accountId, song, item, label, entries, compareKeys, dialogEntry, currentUserId } = props;
  const { onCloseCompare, onCloseDialog, onMoreOptions, onToast } = props;
  const intl = useIntl();
  const release = useGetReleaseKitQuery({ accountId, catalogItemUuid: song.uuid }, { skip: dialogEntry === null });
  const songThemeId: ReleaseThemeId | undefined = release.data?.kit?.look.themeId ?? dialogEntry?.releaseThemeId;
  const made = useCallback(
    (number: number) => {
      onCloseDialog();
      onToast(intl.formatMessage(revisionMessages.made, { number }));
    },
    [intl, onCloseDialog, onToast]
  );
  const moreOptions = useCallback(() => {
    onCloseDialog();
    // The Studio starts from the settings of the revision of the dialog (also one whose file is gone).
    onMoreOptions(dialogEntry?.asset === null || dialogEntry === null ? item : { ...item, asset: dialogEntry.asset });
  }, [dialogEntry, item, onCloseDialog, onMoreOptions]);

  return (
    <>
      {compareKeys !== null && (
        <RevisionCompare
          entries={entries}
          initialKeys={compareKeys}
          currentUserId={currentUserId}
          onClose={onCloseCompare}
        />
      )}
      {dialogEntry !== null && songThemeId !== undefined && (
        <NewRevisionDialog
          accountId={accountId}
          catalogItemUuid={song.uuid}
          songTitle={song.title}
          assetLabel={label}
          entry={dialogEntry}
          entries={entries}
          songThemeId={songThemeId}
          onClose={onCloseDialog}
          onMade={made}
          onMoreOptions={moreOptions}
        />
      )}
    </>
  );
};

export const ViewerRevisionLayers = TypedMemo(ViewerRevisionLayersComponent);
